import { Injectable } from '@angular/core';

export interface BrandingConfig {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
}

export type BrandingResponse = Record<string, BrandingConfig>;

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private _logoUrl: string | null = null;

  get logoUrl(): string | null {
    return this._logoUrl;
  }

  applyBranding(config: BrandingConfig) {
    if (config.logoUrl !== undefined) {
      this._logoUrl = config.logoUrl;
    }

    const root = document.documentElement;

    // Primary: main interactive accent (primary action, selected state, focus,
    // success). Secondary: supporting brand presence (page wash, device frame).
    // The neutral surface/text tokens in styles.css are left untouched so the
    // UI stays readable regardless of how unusual/bright/dark either color is.
    this._setColorTokens(root, '--gs-accent', config.primaryColor);
    this._setColorTokens(root, '--gs-accent-2', config.secondaryColor);
  }

  private _setColorTokens(
    root: HTMLElement,
    prefix: string,
    color: string,
  ): void {
    root.style.setProperty(prefix, color);
    root.style.setProperty(`${prefix}-contrast`, this._getContrastColor(color));
    root.style.setProperty(`${prefix}-fg`, this._getSafeForegroundColor(color));
  }

  private _normalizeHex(hexColor: string): string {
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
      // Expand CSS shorthand (e.g. "#000" -> "000000") before parsing.
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    return hex.substring(0, 6);
  }

  private _brightness(hexColor: string): number {
    const hex = this._normalizeHex(hexColor);

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }

  private _getContrastColor(hexColor: string): string {
    // Text/icon drawn on top of a color FILLED with `hexColor` — pick
    // whichever of black/white reads clearly against it. A coarse
    // perceived-brightness split is enough for this binary choice.
    return this._brightness(hexColor) > 0.5 ? '#000000' : '#ffffff';
  }

  private _relativeLuminance(hexColor: string): number {
    const hex = this._normalizeHex(hexColor);
    const channel = (hexPair: string) => {
      const s = parseInt(hexPair, 16) / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const r = channel(hex.substring(0, 2));
    const g = channel(hex.substring(2, 4));
    const b = channel(hex.substring(4, 6));
    // Standard WCAG relative-luminance weights — plain arithmetic, no library.
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private _getSafeForegroundColor(hexColor: string): string {
    // `hexColor` used AS a foreground (icon/glyph) directly on our light
    // neutral page/card surfaces. Whether a color reads clearly there
    // doesn't track simple "brightness" reliably — e.g. pure green
    // (#00ff00) and a mid grey (#999999) both look "medium" by a naive
    // perceived-brightness average, but pure green is nearly as
    // low-contrast against white as pale pastels are. So this compares the
    // WCAG contrast ratio against white (our lightest surface — if it
    // clears the bar there, it clears it against our slightly-off-white
    // surfaces too) to the WCAG 1.4.11 non-text minimum (3:1), instead of
    // reusing the coarser brightness split above.
    const contrastAgainstWhite =
      (1 + 0.05) / (this._relativeLuminance(hexColor) + 0.05);
    return contrastAgainstWhite >= 3 ? hexColor : '#18181b';
  }
}
